import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { PlayIcon, RefreshIcon } from "@heroicons/react/outline";
import axios from "axios";
import { API_ROUTES, buildAPIRoute } from "../../router/router";
import { HTTPFormProps } from "./HTTPForm";
import { formatResponseBody, isBodyJson } from "./HTTPFormUtils";

interface HTTPFormTestRequestProps {
  formValues: HTTPFormProps["formValues"];
}

interface TestResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: any;
  error?: string;
}

export default function HTTPFormTestRequest({
  formValues,
}: HTTPFormTestRequestProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<TestResponse | null>(null);
  const [expanded, setExpanded] = useState<"headers" | "body" | null>(null);

  const handleTestRequest = async () => {
    setIsLoading(true);
    setResponse(null);

    try {
      const requestData = {
        action_request_method: formValues.method,
        action_request_url: formValues.url,
        action_request_body: "",
        action_request_headers: {},
        action_request_params: {},
      };

      if (formValues.headers && formValues.headers.length > 0) {
        formValues.headers.forEach((header) => {
          if (header.key) {
            requestData.action_request_headers[header.key] = header.value || "";
          }
        });
      }

      if (formValues.params && formValues.params.length > 0) {
        formValues.params.forEach((param) => {
          if (param.key) {
            requestData.action_request_params[param.key] = param.value || "";
          }
        });
      }

      if (
        formValues.body_type === "application/json" ||
        formValues.body_type === "application/xml" ||
        formValues.body_type === "text/yaml" ||
        formValues.body_type === "text/plain"
      ) {
        requestData.action_request_body = formValues.body || "";
      } else if (
        formValues.body_type === "multipart/form-data" ||
        formValues.body_type === "application/x-www-form-urlencoded"
      ) {
        if (formValues.body_params && formValues.body_params.length > 0) {
          const bodyParamsObj = {};
          formValues.body_params.forEach((param) => {
            if (param.key) {
              bodyParamsObj[param.key] = param.value || "";
            }
          });
          requestData.action_request_body = JSON.stringify(bodyParamsObj);
        }
      }

      const proxyResponse = await axios.post(
        buildAPIRoute(API_ROUTES.INBOX_ACTION_TEST_HTTP_REQUEST),
        requestData
      );

      const responseData = proxyResponse.data;

      setExpanded("body");

      setResponse({
        status: responseData.response.status || 0,
        statusText:
          responseData.response.statusText ||
          (responseData.response.status
            ? responseData.response.status.toString()
            : ""),
        headers: responseData.response.headers || {},
        body: responseData.response.response,
      });
    } catch (error: unknown) {
      let errorMessage = "Failed to make test request";
      let statusCode = 0;
      let statusText = "Error";

      if (axios.isAxiosError(error) && error.response) {
        statusCode = error.response.status;
        statusText = error.response.statusText;
        errorMessage = `Error: ${statusCode} ${statusText}`;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setResponse({
        status: statusCode,
        statusText: statusText,
        headers: {},
        body: null,
        error: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpanded = (section: "headers" | "body") => {
    if (expanded === section) {
      setExpanded(null);
    } else {
      setExpanded(section);
    }
  };

  const getStatusColor = (status: number) => {
    if (!status) return "bg-gray-500";
    if (status >= 200 && status < 300) return "bg-green-500";
    if (status >= 300 && status < 400) return "bg-blue-500";
    if (status >= 400 && status < 500) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <div className="flex flex-col">
      <button
        className="inline-flex items-center rounded border border-blue-300 bg-white px-2.5 py-1.5 text-xs font-medium text-blue-700 shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        type="button"
        onClick={handleTestRequest}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <RefreshIcon className="mr-1 h-4 w-4 animate-spin" />
            {t("inbox-actions:builder.testing", "Testing...")}
          </>
        ) : (
          <>
            <PlayIcon className="mr-1 h-4 w-4" />
            {t("inbox-actions:builder.test-request", "Test Request")}
          </>
        )}
      </button>

      {response && (
        <div className="mt-4 flex flex-col overflow-hidden rounded-md border border-gray-200 bg-white">
          {/* Status */}
          <div className="flex items-center border-b border-gray-200 p-2">
            <div
              className={`mr-2 h-3 w-3 rounded-full ${getStatusColor(
                response.status
              )}`}
            ></div>
            <span className="font-mono text-sm font-medium">
              {response.status}
            </span>
          </div>

          {response.error && (
            <div className="border-b border-gray-200 bg-red-50 p-2 text-sm text-red-700">
              {response.error}
            </div>
          )}

          {/* Headers */}
          <div className="border-b border-gray-200">
            <button
              type="button"
              className="flex w-full items-center justify-between p-2 text-left text-sm font-medium hover:bg-gray-50"
              onClick={() => toggleExpanded("headers")}
            >
              <span>Headers</span>
              <span className="text-xs text-gray-500">
                {expanded === "headers" ? "Hide" : "Show"}
              </span>
            </button>

            {expanded === "headers" && (
              <div className="bg-gray-50 p-2">
                {Object.entries(response.headers || {}).length > 0 ? (
                  <div className="max-h-48 overflow-y-auto">
                    {Object.entries(response.headers || {}).map(
                      ([key, value]) => (
                        <div key={key} className="mb-1 flex">
                          <span className="mr-2 font-mono text-xs font-medium text-gray-600">
                            {key}:
                          </span>
                          <span className="font-mono text-xs">{value}</span>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    {t("inbox-actions:builder.no-headers", "No headers")}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Body */}
          <div>
            <button
              type="button"
              className="flex w-full items-center justify-between p-2 text-left text-sm font-medium hover:bg-gray-50"
              onClick={() => toggleExpanded("body")}
            >
              <span>Body</span>
              <span className="text-xs text-gray-500">
                {expanded === "body" ? "Hide" : "Show"}
              </span>
            </button>

            {expanded === "body" && (
              <div className="bg-gray-50 p-2">
                <div className="max-h-60 overflow-y-auto">
                  {isBodyJson(response.body) ? (
                    <SyntaxHighlighter
                      language="json"
                      style={oneLight}
                      customStyle={{
                        fontSize: "12px",
                        margin: 0,
                        padding: 0,
                        backgroundColor: "transparent",
                      }}
                      wrapLines={true}
                      lineProps={{ style: { whiteSpace: "pre-wrap" } }}
                    >
                      {formatResponseBody(response.body)}
                    </SyntaxHighlighter>
                  ) : (
                    <pre className="whitespace-pre-wrap break-all font-mono text-xs">
                      {formatResponseBody(response.body)}
                    </pre>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
